import { runtimeConfig } from "@/shared/config/runtime";

const NCALAYER_WS = "wss://127.0.0.1:13579";
const NCALAYER_TIMEOUT_MS = 120_000;
const DEMO_SIGNATURE = "DEMO-SIGNATURE";

export interface NCALayerResult {
  signature: string;
}

export class NCALayerError extends Error {
  code?: string;

  constructor(messageKey: string, code?: string) {
    super(messageKey);
    this.name = "NCALayerError";
    this.code = code;
  }
}

export function signWithNCALayer(dataBase64: string): Promise<NCALayerResult> {
  if (runtimeConfig.isDemo) {
    return Promise.resolve({ signature: DEMO_SIGNATURE });
  }

  return new Promise((resolve, reject) => {
    let ws: WebSocket;

    try {
      ws = new WebSocket(NCALAYER_WS);
    } catch {
      reject(new NCALayerError("ncalayer.errors.cannotConnect"));
      return;
    }

    let handshakeConsumed = false;

    const timeout = setTimeout(() => {
      ws.close();
      reject(new NCALayerError("ncalayer.errors.timeout"));
    }, NCALAYER_TIMEOUT_MS);

    ws.onopen = () => {};

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (!handshakeConsumed) {
          handshakeConsumed = true;

          const request = {
            module: "kz.gov.pki.knca.commonUtils",
            method: "createCAdESFromBase64",
            args: ["PKCS12", "SIGNATURE", dataBase64, false],
          };
          ws.send(JSON.stringify(request));
          return;
        }

        clearTimeout(timeout);
        ws.close();

        if (data.code === "200" && data.responseObject) {
          resolve({ signature: data.responseObject });
        } else if (data.code === "500") {
          reject(new NCALayerError("ncalayer.errors.cancelled", data.code));
        } else {
          reject(new NCALayerError("ncalayer.errors.unknown", data.code));
        }
      } catch {
        clearTimeout(timeout);
        ws.close();
        reject(new NCALayerError("ncalayer.errors.parseFailed"));
      }
    };

    ws.onerror = () => {
      clearTimeout(timeout);
      reject(new NCALayerError("ncalayer.errors.cannotConnect"));
    };

    ws.onclose = (event) => {
      clearTimeout(timeout);
      if (!handshakeConsumed) {
        reject(new NCALayerError("ncalayer.errors.connectionClosed", String(event.code)));
      }
    };
  });
}
